import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useFirestore } from "../../firebase/FirestoreContext";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { EnvContext } from "../../../context/EnvContext";
import config from "../../../config";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Switch } from "@mui/material";

const EditModal = ({}) => {
  const { id } = useParams();
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    actionButtonText: "",
    actionUrl: "",
    showOnce: false,
    imageUrl: "",
    imageName: "",
    active: false,
  });
  const [loading, setLoading] = useState(true);

  const { fetchModals } = useOutletContext();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  useEffect(() => {
    const fetchModal = async () => {
      const docRef = doc(db, `${collectionPath}Modals/CustomModals/DataBase`, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFormData({
          ...data,
          imageName: data.imageUrl ? data.imageUrl.split("/").pop() : "",
        });
        setLoading(false);
      } else {
        alert("Modal not found");
        navigate("/main/modals");
      }
    };
    fetchModal();
  }, [db, id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedData = {
      ...formData,
      active: formData.active, // Ensure active status is included
    };
    const docRef = doc(db, `${collectionPath}Modals/CustomModals/DataBase`, id);
    await updateDoc(docRef, updatedData);
    fetchModals();
    navigate("/main/modals"); // Navigate back to modals list after saving
  };

  const handleSwitchChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      active: event.target.checked,
    }));
  };

  // Image upload handler
  const handleUploadImage = async (file) => {
    if (!file) return;

    try {
      const storageRef = ref(storage, `ModalsCustom/${id}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Update Firestore with new image URL
      const docRef = doc(db, `${collectionPath}Modals/CustomModals/DataBase`, id);
      await updateDoc(docRef, { imageUrl: url });

      setFormData((prev) => ({ ...prev, imageUrl: url, imageName: file.name }));
      console.log("Image uploaded successfully.");
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Edit Modal</h2>
      </div>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Title:</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} style={styles.input} required />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Content:</label>
          <textarea name="content" value={formData.content} onChange={handleChange} style={styles.textarea} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Image:</label>
          {formData.imageName ? (
            <div style={styles.imageContainer}>
              <img src={formData.imageUrl} alt="Modal" style={styles.thumbnail} />
              <input type="file" onChange={(e) => handleUploadImage(e.target.files[0])} accept="image/*" />
            </div>
          ) : (
            <input type="file" onChange={(e) => handleUploadImage(e.target.files[0])} accept="image/*" />
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Target URL:</label>
          <input type="text" name="targetUrl" value={formData.targetUrl} onChange={handleChange} style={styles.input} />
          <small style={{ ...styles.helperText, color: "silver" }}>e.g., Listing/LYRTaOxwuevmXGJn1xxz_FTLCgZhDhRM8KdeOC7yM</small>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Action Button Text:</label>
          <input type="text" name="actionButtonText" value={formData.actionButtonText} onChange={handleChange} style={styles.input} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Active:</label>
          <Switch checked={formData.active || false} onChange={handleSwitchChange} color="primary" />
        </div>

        <div style={styles.formActions}>
          <button type="button" style={{ ...styles.button, backgroundColor: "silver", marginRight: 10 }} onClick={() => navigate("/main/modals")}>
            Cancel
          </button>
          <button type="submit" style={styles.button}>
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: { margin: "0 auto", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" },
  header: { marginBottom: "20px", borderBottom: "1px solid #ccc", paddingBottom: "10px" },
  form: { display: "flex", flexDirection: "column", gap: "15px" },
  formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
  label: { fontSize: "14px", fontWeight: "bold" },
  input: { padding: "10px", fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px" },
  textarea: { padding: "10px", fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px", minHeight: "80px" },
  checkbox: { transform: "scale(1.2)" },
  formActions: { display: "flex", justifyContent: "flex-end" },
  button: { backgroundColor: "black", color: "white", border: "none", padding: "10px 20px", cursor: "pointer", borderRadius: "4px" },
  imageContainer: { display: "flex", alignItems: "center", gap: "10px" },
  thumbnail: { width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" },
};

export default EditModal;
